import type { PageResponse } from './common'

export type WorkOrderStatus =
  | 'CREATED'
  | 'REJECTED'
  | 'ACCEPTED'
  | 'DISPATCHED'
  | 'DIAGNOSING'
  | 'WAITING_QUOTE'
  | 'QUOTING'
  | 'WAITING_CONFIRM'
  | 'WAITING_PARTS'
  | 'READY_FOR_REPAIR'
  | 'IN_REPAIR'
  | 'WAITING_ACCEPTANCE'
  | 'COMPLETED'
  | 'REOPENED'
  | 'CANCELLED'
  | 'CLOSED_EXCEPTION'

export const WORK_ORDER_STATUS_LABEL: Record<WorkOrderStatus, string> = {
  CREATED: '待接收',
  REJECTED: '已拒绝',
  ACCEPTED: '已接收',
  DISPATCHED: '已派单',
  DIAGNOSING: '诊断中',
  WAITING_QUOTE: '待报价',
  QUOTING: '报价中',
  WAITING_CONFIRM: '待确认',
  WAITING_PARTS: '待配件',
  READY_FOR_REPAIR: '待维修',
  IN_REPAIR: '维修中',
  WAITING_ACCEPTANCE: '待验收',
  COMPLETED: '已完成',
  REOPENED: '已重开',
  CANCELLED: '已取消',
  CLOSED_EXCEPTION: '异常关闭',
}

export const WORK_ORDER_STATUS_COLOR: Record<WorkOrderStatus, string> = {
  CREATED: 'default',
  REJECTED: 'error',
  ACCEPTED: 'processing',
  DISPATCHED: 'blue',
  DIAGNOSING: 'blue',
  WAITING_QUOTE: 'orange',
  QUOTING: 'orange',
  WAITING_CONFIRM: 'gold',
  WAITING_PARTS: 'purple',
  READY_FOR_REPAIR: 'cyan',
  IN_REPAIR: 'blue',
  WAITING_ACCEPTANCE: 'geekblue',
  COMPLETED: 'success',
  REOPENED: 'warning',
  CANCELLED: 'default',
  CLOSED_EXCEPTION: 'error',
}

export type UrgencyLevel = 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT'

export const URGENCY_LABEL: Record<UrgencyLevel, string> = {
  LOW: '低',
  NORMAL: '普通',
  HIGH: '紧急',
  URGENT: '加急',
}

export const URGENCY_COLOR: Record<UrgencyLevel, string> = {
  LOW: 'default',
  NORMAL: 'processing',
  HIGH: 'warning',
  URGENT: 'error',
}

export interface DispatchRecord {
  id: number
  engineerId: string
  engineerName: string
  status: 'ASSIGNED' | 'REASSIGNED' | 'COMPLETED' | 'REJECTED' | 'ACCEPTED' | 'DEPARTED' | 'ARRIVED'
  remark?: string
  currentActive?: boolean
  createdAt: string
}

export interface DiagnosisRecord {
  id: number
  roundNo: number
  engineerId: string
  engineerName: string
  mode: 'REMOTE' | 'ONSITE'
  status: 'PENDING' | 'STARTED' | 'IN_PROGRESS' | 'COMPLETED'
  problemClear?: boolean
  needContinueVisit?: boolean
  needParts?: boolean
  faultCategory?: string
  suggestion?: string
  summary?: string
  attachments?: string[]
  createdAt: string
}

export interface RepairRecord {
  id: number
  roundNo: number
  engineerId: string
  engineerName: string
  status: 'PENDING' | 'STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'MISSING_PARTS' | 'REDIAGNOSIS_REQUESTED'
  summary?: string
  selfCheckPassed?: boolean
  selfCheckResult?: string
  attachments?: string[]
  createdAt: string
}

export interface AcceptanceRecord {
  id: number
  roundNo: number
  acceptedBy: string
  acceptedByName: string
  result: 'PASSED' | 'FAILED'
  remark?: string
  attachments?: string[]
  createdAt: string
}

export interface QuotationSummary {
  id: number
  workOrderId: number
  versionNo: number
  quoteNo?: string
  status: string
  totalAmount: number
  originalPrice?: number
  discountAmount?: number
  currency: string
  estimatedDuration?: string
  salesName?: string
  createdAt: string
}

export interface PartRequirement {
  id: number
  skuCode: string
  partName: string
  requiredQty: number
  reservedQty: number
  consumedQty: number
  critical?: boolean
  source?: string
}

export interface ActionLog {
  id: number
  actionType: string
  fromStatus?: string
  toStatus?: string
  operatorId?: string
  operatorName?: string
  operatorRole?: string
  remark?: string
  createdAt: string
}

export const ACTION_TYPE_LABEL: Record<string, string> = {
  CREATE: '创建工单',
  ACCEPT: '接收工单',
  REJECT: '拒绝工单',
  DISPATCH: '派单',
  REDISPATCH: '改派',
  START_DIAGNOSIS: '开始诊断',
  COMPLETE_DIAGNOSIS: '完成诊断',
  PREPARE_PARTS: '备件准备',
  AUTO_READY_FOR_REPAIR: '备件齐套',
  START_REPAIR: '开始维修',
  COMPLETE_REPAIR: '完成维修',
  REQUEST_REDIAGNOSIS: '申请重新诊断',
  REPORT_MISSING_PARTS: '上报缺件',
  ACCEPTANCE_PASSED: '验收通过',
  ACCEPTANCE_FAILED: '验收失败',
  CANCEL: '取消工单',
  CLOSE_EXCEPTION: '异常关闭',
  REOPEN: '重开工单',
  BIND_DEVICE_ASSET: '绑定设备',
  ACCEPT_DISPATCH: '接单',
  DEPART: '出发',
  ARRIVE: '到场',
}

export interface DeviceAssetSummary {
  id: number
  assetCode: string
  deviceName: string
  deviceModel: string
  deviceSerialNo: string
}

export interface WorkOrderPurchaseOrderSummary {
  id: number
  purchaseNo: string
  status: string
  supplierName: string
  expectedDeliveryDate?: string | null
  warehouseCode?: string | null
  trackingNo?: string | null
  remark?: string | null
  workOrderNo?: string | null
  createdAt: string
}

export interface WorkOrder {
  id: number
  orderNo: string
  title: string
  status: WorkOrderStatus
  sourceType?: string
  customerType?: string
  customerName?: string
  hospitalName?: string
  contactName?: string
  contactPhone?: string
  deviceId?: number
  deviceAsset?: DeviceAssetSummary
  deviceName?: string
  deviceModel?: string
  deviceSerialNo?: string
  installLocation?: string
  department?: string
  attachments?: string[]
  faultDesc?: string
  urgencyLevel?: UrgencyLevel
  reopenCount?: number
  createdAt?: string
  updatedAt?: string
  dispatches?: DispatchRecord[]
  diagnoses?: DiagnosisRecord[]
  quotations?: QuotationSummary[]
  partRequirements?: PartRequirement[]
  purchaseOrders?: WorkOrderPurchaseOrderSummary[]
  repairs?: RepairRecord[]
  acceptances?: AcceptanceRecord[]
  actions?: ActionLog[]
}

export type WorkOrderListItem = Pick<
  WorkOrder,
  | 'id'
  | 'orderNo'
  | 'title'
  | 'status'
  | 'hospitalName'
  | 'deviceName'
  | 'deviceModel'
  | 'faultDesc'
  | 'urgencyLevel'
  | 'contactName'
  | 'contactPhone'
> & {
  createdAt: string
  updatedAt?: string
}

export type WorkOrderPageResponse = PageResponse<WorkOrderListItem>

export interface DispatchRequest {
  engineerId: string
  engineerName: string
  remark?: string
}

export interface ReasonRequest {
  reason: string
}

export interface PartItemRequest {
  skuCode: string
  partName: string
  quantity: number
  critical?: boolean
}

export interface BindDeviceAssetRequest {
  deviceAssetId: number
}

export interface StartDiagnosisRequest {
  mode: 'REMOTE' | 'ONSITE'
}

export interface CompleteDiagnosisRequest {
  problemClear: boolean
  needContinueVisit: boolean
  needParts: boolean
  faultCategory?: string
  suggestion?: string
  summary: string
  attachments?: string[]
}

export interface PreparePartsRequest {
  source: string
  parts: PartItemRequest[]
}

export interface RepairStartRequest {
  summary: string
}

export interface RepairCompleteRequest {
  summary: string
  selfCheckPassed: boolean
  attachments?: string[]
}

export interface AcceptanceRequest {
  remark: string
  attachments?: string[]
}

export interface WorkOrderFilter {
  status?: string
  keyword?: string
  sourceType?: string
  engineerId?: string
  createdFrom?: string
  createdTo?: string
  page?: number
  size?: number
}
